import Service from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { ajax } from 'discourse/lib/ajax';

// Simple CRC32 hash function for string hashing
const crcTable = new Uint32Array(256);
for (let i = 0; i < 256; i++) {
  let c = i;
  for (let j = 0; j < 8; j++) {
    c = ((c & 1) ? (0xEDB88320 ^ (c >>> 1)) : (c >>> 1));
  }
  crcTable[i] = c;
}

function crc32(str) {
  let crc = 0 ^ (-1);
  for (let i = 0; i < str.length; i++) {
    crc = (crc >>> 8) ^ crcTable[(crc ^ str.charCodeAt(i)) & 0xFF];
  }
  return (crc ^ (-1)) >>> 0;
}

export default class ProxyviewsExperiments extends Service {
  @tracked experiments = [];
  @tracked isReady = false;
  
  clientId = null;

  constructor() {
    super(...arguments);
    this.initClientId();
    this.fetchExperiments();
  }

  initClientId() {
    let id = localStorage.getItem('proxyviews_client_id');
    if (!id) {
      id = crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).substring(2) + Date.now().toString(36);
      localStorage.setItem('proxyviews_client_id', id);
    }
    this.clientId = id;
  }

  async fetchExperiments() {
    try {
      const result = await ajax('/proxyviews/analytics/experiments/active');
      this.experiments = result.experiments || [];
    } catch (e) {
      // Silently fail if unable to fetch experiments (e.g., ad blocker)
    } finally {
      this.isReady = true;
    }
  }

  /**
   * Get the assigned variant object for a specific experiment name.
   * Returns null if the user is not in the experiment or if it's inactive.
   */
  getVariant(experimentName) {
    const experiment = this.experiments.find(e => e.name === experimentName);
    if (!experiment) return null;

    // Use deterministic hash to assign the user a number from 0 to 99
    const hashValue = crc32(`${this.clientId}-${experiment.id}`);
    const roll = hashValue % 100; // 0-99

    // Check if user falls into traffic allocation
    if (roll >= experiment.traffic_allocation) {
      return null; // Control group (unassigned)
    }

    // Hash again to pick the variant so variant selection is independent of traffic allocation roll
    const variantRoll = crc32(`${this.clientId}-${experiment.id}-variant`) % 10000;
    
    // Calculate total weight
    const totalWeight = experiment.variants.reduce((sum, v) => sum + v.weight, 0);
    if (totalWeight === 0) return null;

    let target = (variantRoll / 10000) * totalWeight;
    
    for (const variant of experiment.variants) {
      target -= variant.weight;
      if (target < 0) {
        return variant;
      }
    }
    
    // Fallback to first
    return experiment.variants[0];
  }

  /**
   * Returns a map of all assigned variants for analytics tracking
   * e.g. { "directory_cta_color": "red_button" }
   */
  getAllAssignments() {
    const assignments = {};
    for (const experiment of this.experiments) {
      const variant = this.getVariant(experiment.name);
      if (variant) {
        assignments[experiment.name] = variant.name;
      }
    }
    return assignments;
  }
}
